/* eslint-disable max-lines */
import Environments, { APIs } from '@delta-defense/client-environments';
import { DoUntil } from '@delta-defense/delta-utils';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import * as z from 'zod/v4';
import { API } from '../../api/API';
import { passwordComplexityRequirements, passwordRegex } from '../../constants';
import { Cookies, CookieService } from '../../core/services/cookieService';
import { getAnalyticsProperties } from '../../utilities/getAnalyticsProperties';
import { identifyUser } from '../../utilities/loadSegment';
import Question from '../AdditionalInfo/Question';
import AuthAlert from '../AuthAlert/AuthAlert';
import AuthButton from '../AuthButton/AuthButton';
import AccountCreated from '../CompletedForms/AccountCreated';
import { DefaultErrorPage } from '../ErrorPage/defaultErrorPage';
import { InputGroup } from '../InputGroup/InputGroup';
import Loading from '../Loading/Loading';
import { Title } from '../Title/Title';
import './CreateAccount.scss';

declare global {
  interface Window {
    deltaShaftComplianceVerbiage?: string;
  }
}

type CreateAccountProps = {
  cookieService: ReturnType<typeof CookieService.Instance>
}
export function CreateAccount(props: CreateAccountProps): React.JSX.Element {
  const [accountCreated, setAccountCreated] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [firstNameError, setFirstNameError] = useState<string | null>(null);
  const [lastName, setLastName] = useState('');
  const [lastNameError, setLastNameError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [passwordConfirmationError, setPasswordConfirmationError] = useState<string | null>(null);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const [shaftVerbiage, setShaftVerbiage] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    DoUntil(() => !!window.deltaShaftComplianceVerbiage, 100, 3000)
      .then(() => setShaftVerbiage(window.deltaShaftComplianceVerbiage || ''));

    return () => {
      setLoading(false);
    };
  }, []);

  async function onSubmitHandler(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    clearErrors();
    setLoading(true);

    if (!isUserInputValid()) {
      setLoading(false);
      return;
    }

    const [tracking, meta] = await Promise.all([
      getLeadFields(),
      getMetadata()
    ]);
    const newUserEndpoint = `${Environments.getProxyEndpointForApi(APIs.Auth)}/users`;

    type NewUser = {
      name: string;
      surname: string;
      email: string;
      phone?: string;
      password: string;
      tracking: Awaited<ReturnType<typeof getLeadFields>>;
      meta: Awaited<ReturnType<typeof getMetadata>>;
    }
    const newUser: NewUser = {
      name: firstName,
      surname: lastName,
      email,
      password,
      tracking,
      meta
    };


    if (phone) {
      const numbersOnlyPhoneValue = phone.replace(/[^0-9]*/g, '');
      if (numbersOnlyPhoneValue.length === 10) {
        newUser.phone = `${numbersOnlyPhoneValue.slice(0, 3)}-${numbersOnlyPhoneValue.slice(3, 6)}-${numbersOnlyPhoneValue.slice(6)}`;
      }
    }

    const response = await API.post(newUserEndpoint, newUser);

    if (response.status === 201) {
      const body = await response.json();

      const { token, ttl } = body.included[0].attributes;
      const user = body.data.attributes;
      const userId = props.cookieService.decodeToken(token)?.userId;
      identifyUser({
        userId,
        ...user
      });
      await props.cookieService.setAccessToken(token, ttl);

      setAccountCreated(true);
    } else if (response.status !== 201) {
      setErrorStatus(response.status);
    }

    setLoading(false);
  }

  function isUserInputValid(): boolean {
    if (firstName.length < 2) {
      setFirstNameError('First name must be at least 2 characters.');
      return false;
    }

    if (lastName.length < 2) {
      setLastNameError('Last name must be at least 2 characters.');
      return false;
    }

    if (!validateEmail(email)) {
      setEmailError('Email is invalid.');
      return false;
    }

    if (phone && !validatePhone(phone)) {
      setPhoneError('Phone number format is invalid.');
      return false;
    }

    if (!passwordRegex.test(password)) {
      setPasswordError('Password must meet complexity requirements.');
      return false;
    }

    if (passwordConfirmation !== password) {
      setPasswordConfirmationError("Passwords don't match.");
      return false;
    }

    return true;
  }

  function validateEmail(email: string): boolean {
    try {
      z.email().parse(email);
      return true;
    } catch (error) {
      return false;
    }
  }

  function validatePhone(phone: string): boolean {
    try {
      z.string().regex(/^[\d]{3}-?[\d]{3}-?[\d]{4}$/).parse(phone);
      return true;
    } catch (error) {
      return false;
    }
  }

  type Tracking = {
    tID: string;
    origin: string;
    medium: string;
    campaign: string;
    audience: string;
    targeting: string;
    type: string;
    pageType: string;
  }
  async function getLeadFields(): Promise<Tracking> {
    const defaultTracking = {
      tID: 'genf8f5f0c9e0',
      origin: 'Unknown',
      medium: '_',
      campaign: '_',
      audience: '_',
      targeting: '_',
      type: 'New Account',
      pageType: '_'
    } satisfies Tracking;

    const leadFieldsCookie = props.cookieService.getRaw(Cookies.USCCA_Leads);
    const leadToken = props.cookieService.decodeToken(leadFieldsCookie);
    let tracking: typeof defaultTracking;
    if (leadToken) {
      tracking = leadToken;
    } else {
      tracking = defaultTracking;
      await props.cookieService.setLeadFields(defaultTracking);
    }

    return tracking;
  }

  async function getMetadata(): Promise<typeof meta> {
    const analyticsProperties = await getAnalyticsProperties();
    const meta = {
      label:
        analyticsProperties?._LoginModal_parent_window_href ||
        window.location.href,
      fbc: analyticsProperties?.fbc,
      fbp: analyticsProperties?.fbp,
      gaClickID: analyticsProperties?.ga_click_id,
      gaClientID: analyticsProperties?.ga_client_id,
      twclid: analyticsProperties?.twclid
    };

    return meta;
  }

  function clearErrors(): void {
    setFirstNameError(null);
    setLastNameError(null);
    setEmailError(null);
    setPhoneError(null);
    setPasswordError(null);
    setPasswordConfirmationError(null);
    setErrorStatus(null);
  }

  if (accountCreated) {
    return <AccountCreated />;
  }

  if (errorStatus && errorStatus !== 409) {
    return <DefaultErrorPage errorStatus={errorStatus} />;
  }

  return (
    <>
      <Title name="Create Account" />

      <p className="description">
        Gain access to helpful self-defense guides and checklists, preview
        USCCA's Protector Academy &trade;, talk with like-minded people in the
        Community and much more.
      </p>

      <form
        name='create-account'
        onSubmit={onSubmitHandler}
      >
        <InputGroup
          id='firstName'
          name='firstName'
          label='First Name'
          type='text'
          value={firstName}
          onChange={e => setFirstName(e.target.value)}
          error={firstNameError}
        />

        <InputGroup
          id='lastName'
          name='lastName'
          label='Last Name'
          type='text'
          value={lastName}
          onChange={e => setLastName(e.target.value)}
          error={lastNameError}
        />

        <InputGroup
          id='email'
          name='email'
          label='Email'
          type='email'
          value={email}
          onChange={e => setEmail(e.target.value)}
          error={emailError}
        />
        {errorStatus == 409 && (
          <AuthAlert
            message="The email address you entered is already connected to an account in our system."
            link={
              <Link id="errorSignIn" className="auth-alert__a" to="/">
                Click here to sign in.
              </Link>
            }
          />
        )}


        <InputGroup
          id='phone'
          name='phone'
          label='Phone'
          type='tel'
          optional
          optionalTooltip='Allows the sending of account authorization codes to your mobile device'
          value={phone}
          onChange={e => setPhone(e.target.value)}
          error={phoneError}
        />

        <InputGroup
          id='password'
          name='password'
          label='Password'
          type='password'
          value={password}
          onChange={e => setPassword(e.target.value)}
          error={passwordError}
        />
        <p className="mt-1 mb-0 description">{passwordComplexityRequirements}</p>

        <InputGroup
          id='passwordConfirmation'
          name='passwordConfirmation'
          label='Password Confirmation'
          type='password'
          value={passwordConfirmation}
          onChange={e => setPasswordConfirmation(e.target.value)}
          error={passwordConfirmationError}
        />

        <AuthButton
          id="createAccountSignUpButton"
          color="btn-gold"
          text={loading ? <Loading /> : 'Create Free Account'}
          disabled={loading}
        />
      </form>

      <Question
        headline="Already have an account?"
        link={`/${window.location.search}`}
        text="Log In"
        id="signInTodayButton"
      />

      <p className="shaftVerbiage">{shaftVerbiage}</p>
    </>
  );
}
